/**
 * PDF 빌더 Bar/Line 차트 — 리포트 화면의 Graph(Chart by Player) 섹션과 같은 것 (일감 #75, 4단계).
 *
 * 축 구성(단위·소수 자릿수·색·차트 종류·수동 스케일)·값 추출·정렬·값 표시(dataLabel)는 Report Filters 에서 고른
 * 리포트 템플릿의 Graph 차트 설정을 따르고, 리포트 화면·PDF 와 같은 buildChartData / sortChartRows / createChart 로 만든다.
 * 지표는 처음 추가될 때 그 차트의 지표를 가져와 선택된 상태로 두고 아이템별로 더하고 뺀다 (결정 2026-09-23).
 * 이 파일은 빌더 고유 사정 — 캔버스 폭·높이에 맞춘 세로 분할, 가로 막대 모드, 칸 높이 자동 조정 — 만 다룬다.
 */
import { useEffect, useRef, useMemo, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"

import { usePdfDataContext } from "@/app/export/pdf/contexts/PdfDataContext"
import { usePdfBuilderStore, selectCurrentPageConfig } from "@/stores/pdfBuilder"
import { PAPER_DIMENSIONS } from "@/types/pdfBuilder"
import LoadingSpinner from "@/app/components/LoadingSpinner"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook"
import { usePositionRank } from "@/hooks/usePositionRank"
import {
  ChartData,
  ChartRow,
  ChartMultiple,
  ChartMultipleItem,
} from "@/app/report/common/constructor"
import {
  buildChartData,
  getTemplateGraphCharts,
  resolveGraphOrientation,
  resolveGraphShowAxis,
  resolveTemplateGraphChart,
} from "@/app/report/common/sections/graph"
import {
  buildTrendActivityChartData,
  buildTrendPlayerChartData,
  getTemplateTrendCharts,
} from "@/app/report/common/sections/trendGraph"
import {
  chunkVerticalRows,
  coerceVerticalAxisTypes,
  collectVerticalHighest,
  drawVerticalPlayerChart,
  measureVerticalChartHeight,
  rowHeightFor,
  VERTICAL_PLAYERS_PER_PAGE_DEFAULT,
  VERTICAL_PLAYERS_PER_PAGE_MAX_LANDSCAPE,
  VERTICAL_PLAYERS_PER_PAGE_MAX_PORTRAIT,
} from "@/app/report/common/chart/verticalPlayerChart"
import { sortChartRows } from "@/app/report/common/export/sortRows"
import { ChartStyle } from "@/app/report/common/chart/multiple"
import {
  createChartFilled,
  dataLabelExtraPx,
  dataLabelToValuePosition,
  hasDrawnContent,
  LEGEND_FALLBACK_PX,
} from "./engine/drawEngineChart"
import {
  planPageBlocks,
  GRID_ROW_PX,
  CANVAS_TOP_PX,
  PAGE_MARGIN_PX,
  isManualLayout,
} from "./engine/pageBlocks"
import { VALUE_ORNAMENT_TYPE, VALUE_POSITION_TYPE } from "@/app/report/common/constants"
import { colors } from "@/components/colors"
import type { GraphOrientation, GraphSettings } from "@/app/report/common/type"
import type { ChartComponentProps } from "./types"

import "./builderCharts.scss"

const DEFAULT_COLORS = [
  colors.blue["800"],
  colors.green["700"],
  colors.orange["700"],
  colors.pink["800"],
  colors.purple["900"],
  colors.primary["600"],
]

/** Chart by Player·Session Details 한 아이템의 지표 상한 (2026-09-28 요청) */
export const MAX_CHART_SERIES = 3

interface BuilderD3ChartProps extends ChartComponentProps {
  defaultChartType: string
  /**
   * 혼합(Combo) 차트: 축 종류를 팔레트 종류 하나로 강제하지 않고 지표별로 정한다 —
   * 아이템의 metricChartTypes → 템플릿 축 종류 → defaultChartType 순. 가로 막대 모드는 쓰지 않는다.
   */
  followTemplateChartType?: boolean
  /** 트렌드 리포트에서 한 줄이 무엇인지 — 선수(Chart by Player) / 액티비티(Chart by Activity) (2026-10-02 요청) */
  trendRows?: "player" | "activity"
}

// CanvasItem 크롬: 테두리 4 + 안쪽 여백 16 + 머리말 ≈ 30
const ITEM_CHROME_PX = 50

export default function BuilderD3Chart({
  item,
  width,
  defaultChartType,
  followTemplateChartType = false,
  trendRows = "player",
}: BuilderD3ChartProps) {
  const { t } = useTranslation()
  const {
    playerSummary,
    reportTemplate,
    metricMap,
    unitList,
    hasActivitySelection,
    resolveItemPlayerIds,
    isLoading,
    reportKind,
    trendPlayers,
    trendActivities,
  } = usePdfDataContext()
  const isTrend = reportKind === "trend"
  const currentPageId = usePdfBuilderStore((s) => s.currentPageId)
  const updateItemLayout = usePdfBuilderStore((s) => s.updateItemLayout)
  const updateItem = usePdfBuilderStore((s) => s.updateItem)
  const translateKey = useTranslateMetricKey()
  const positionRank = usePositionRank()
  const isExporting = usePdfBuilderStore((s) => s.isExporting)
  const setHeadGap = usePdfBuilderStore((s) => s.setHeadGap)
  const pageConfig = usePdfBuilderStore(selectCurrentPageConfig)
  const pageDims = PAPER_DIMENSIONS[pageConfig?.size === "letter" ? "letter" : "a4"]
  const pageHeight = pageConfig?.orientation === "landscape" ? pageDims.width : pageDims.height
  // 세로 차트 영역이 칸 위에서 얼마나 내려와 시작하는지(머리말·범례 아래, 레이아웃 px) — 그린 뒤 잰다
  const [verticalTopPx, setVerticalTopPx] = useState(0)

  const verticalWrapperRef = useRef<HTMLDivElement>(null)

  // ── 설정: 리포트 템플릿의 Graph 차트 (아이템이 가리키는 것, 없으면 첫 차트) ──
  // 트렌드는 선수 그래프(playerGraph)·액티비티 그래프(activityGraph) 섹션에서 차트를 찾는다 (2026-10-02 요청)
  const templateCharts = useMemo(
    () =>
      isTrend
        ? getTemplateTrendCharts(reportTemplate, trendRows)
        : getTemplateGraphCharts(reportTemplate),
    [isTrend, reportTemplate, trendRows],
  )
  const templateChart = useMemo(
    () =>
      resolveTemplateGraphChart(
        templateCharts,
        item.dataSource.sectionIndex,
        item.dataSource.chartIndex,
      ),
    [templateCharts, item.dataSource.sectionIndex, item.dataSource.chartIndex],
  )

  // 처음 추가된 아이템(metrics 미정)은 템플릿 차트의 지표·색을 선택된 상태로 가져온다. 저장된 아이템은 그대로.
  const itemMetrics = item.dataSource.metrics
  useEffect(() => {
    if (itemMetrics !== undefined || !templateChart || !currentPageId) {
      return
    }
    // Chart by Player(followTemplateChartType)는 지표 최대 3개
    const seeded = followTemplateChartType
      ? templateChart.settings.yAxis.slice(0, MAX_CHART_SERIES)
      : templateChart.settings.yAxis
    updateItem(currentPageId, item.id, {
      dataSource: {
        ...item.dataSource,
        metrics: seeded.map((y) => y.metric),
      },
      options: {
        ...item.options,
        colors: seeded.map((y) => y.color),
        // 소수점 자릿수도 만들 때 스냅샷 — 템플릿 0 은 옛 설정 화면이 "0.1"로 보여 주던 미지정 값이라 1(0.1)로 (2026-10-01)
        decimalPlaces: templateChart.settings.decimalPlace || 1,
        // 최대값(수동 스케일)은 만들 때 스냅샷 — 리포트 템플릿이 바뀌어도 저장된 값을 쓴다 (2026-10-01 결정)
        metricScales: Object.fromEntries(
          seeded.map((y) => [
            y.metric,
            y.manualScaleActive && y.manualScaleValue != null ? y.manualScaleValue : null,
          ]),
        ),
      },
    })
  }, [
    itemMetrics,
    templateChart,
    currentPageId,
    item.id,
    item.dataSource,
    item.options,
    updateItem,
    followTemplateChartType,
  ])

  const metrics = useMemo(() => {
    const keys = itemMetrics ?? templateChart?.settings.yAxis.map((y) => y.metric) ?? []

    return followTemplateChartType ? keys.slice(0, MAX_CHART_SERIES) : keys
  }, [itemMetrics, templateChart, followTemplateChartType])
  const itemColors = item.options.colors
  const metricChartTypes = item.options.metricChartTypes
  const metricScales = item.options.metricScales
  // 차트 방향 — 아이템 옵션(chartOrientation) > 템플릿 Graph 설정 > 가로 (#185). 옛 Bar 아이템의
  // "Column Layout: horizontal"(가로 막대)은 선수 세로 나열과 같은 뜻이라 세로로 읽는다
  const chartOrientation: GraphOrientation =
    item.options.chartOrientation ??
    (item.chartType === "bar" && item.options.orientation === "horizontal"
      ? "vertical"
      : resolveGraphOrientation(templateChart?.settings))
  const isPlayerList = chartOrientation === "vertical"
  const showPlayerListAxis = item.options.showXAxis ?? resolveGraphShowAxis(templateChart?.settings)
  const playersPerPageMax =
    pageConfig?.orientation === "landscape"
      ? VERTICAL_PLAYERS_PER_PAGE_MAX_LANDSCAPE
      : VERTICAL_PLAYERS_PER_PAGE_MAX_PORTRAIT
  const playersPerPage = Math.max(
    1,
    Math.min(playersPerPageMax, item.options.playersPerPage ?? VERTICAL_PLAYERS_PER_PAGE_DEFAULT),
  )
  const showLegend = item.options.showLegend ?? metrics.length > 1

  // 템플릿 차트 설정에 아이템 지표(가감)·색을 덮어쓴다. 템플릿에 있던 지표는 축 설정(단위·수동 스케일)을
  // 그대로 쓰되, 차트 종류는 팔레트에서 고른 것(Bar/Line)이 우선한다 — 템플릿 축의 종류를 그대로 쓰면
  // Line 아이템이 막대로 그려진다. 아이템이 더한 지표는 기본 색으로 축을 만든다.
  const graphSettings = useMemo<GraphSettings | null>(() => {
    if (!templateChart) {
      return null
    }
    const base = templateChart.settings

    return {
      ...base,
      title: item.options.title || base.title,
      // 값 표시 위치·정렬·소수점 자릿수는 아이템 옵션이 우선, 없으면 템플릿 (#185)
      dataLabel: item.options.dataLabel ?? base.dataLabel,
      decimalPlace: item.options.decimalPlaces ?? (base.decimalPlace || 1),
      sortSetting:
        item.options.sortKey && item.options.sortKey !== "default"
          ? { key: item.options.sortKey, order: item.options.sortOrder ?? "asc" }
          : base.sortSetting,
      yAxis: metrics.map((metricKey, idx) => {
        const fromTemplate = base.yAxis.find((y) => y.metric === metricKey)
        const color =
          itemColors?.[idx] ?? fromTemplate?.color ?? DEFAULT_COLORS[idx % DEFAULT_COLORS.length]

        // Bar/Line 아이템은 팔레트 종류로 통일(템플릿 축 종류를 그대로 쓰면 Line 아이템이 막대로 그려진다).
        // Combo 아이템은 지표별 지정 → 템플릿 축 종류 → 기본(bar) 순으로 섞어 그린다.
        const chartType = followTemplateChartType
          ? (metricChartTypes?.[metricKey] ?? fromTemplate?.chartType ?? defaultChartType)
          : defaultChartType

        // 최대값 — 아이템에 지표별 값이 있으면(null 은 자동) 그것이 템플릿보다 우선 (2026-10-01 결정)
        const scaleEntry = metricScales?.[metricKey]
        const scale =
          scaleEntry === undefined
            ? null
            : { manualScaleActive: scaleEntry != null, manualScaleValue: scaleEntry }

        return fromTemplate
          ? { ...fromTemplate, color, chartType, ...(scale ?? {}) }
          : {
              metric: metricKey,
              chartType,
              color,
              unit: metricMap.get(metricKey)?.unit,
              manualScaleActive: scale?.manualScaleActive ?? false,
              manualScaleValue: scale?.manualScaleValue ?? null,
            }
      }),
    }
  }, [
    templateChart,
    metrics,
    itemColors,
    metricChartTypes,
    metricScales,
    followTemplateChartType,
    item.options.title,
    item.options.dataLabel,
    item.options.decimalPlaces,
    item.options.sortKey,
    item.options.sortOrder,
    defaultChartType,
    metricMap,
  ])

  // ── 데이터: 리포트와 같은 buildChartData(축·값 추출) + sortChartRows(정렬) ──
  // 트렌드는 값의 출처만 다르다 — 기간 집계를 선수별(Chart by Player) 또는 액티비티별(Chart by Activity)로 (2026-10-02 요청)
  const chartData = useMemo(() => {
    if (!graphSettings || graphSettings.yAxis.length === 0) {
      return null
    }
    const common = {
      settings: graphSettings,
      metricMap,
      selectedUnitList: unitList,
      t,
      translateKey,
    }

    let data: ChartData
    if (isTrend && trendRows === "activity") {
      if (!trendActivities || trendActivities.length === 0) {
        return null
      }
      data = buildTrendActivityChartData({ ...common, activities: trendActivities })
    } else if (isTrend) {
      // 선수 매칭은 idfPlayer 로 한다 — 등번호는 중복·미설정(0)이 가능해 필터가 통째로 무력화된다
      const selectedPlayerIds = new Set(resolveItemPlayerIds(item.dataSource.playerIds))
      const players = (trendPlayers ?? []).filter((p) => selectedPlayerIds.has(p.idfPlayer))
      if (players.length === 0) {
        return null
      }
      data = buildTrendPlayerChartData({ ...common, players })
    } else {
      if (!playerSummary) {
        return null
      }
      const selectedPlayerIds = new Set(resolveItemPlayerIds(item.dataSource.playerIds))
      const players = (playerSummary.players ?? []).filter((p) =>
        selectedPlayerIds.has(p.idfPlayer),
      )
      // Data Source 에서 선수를 전부 해제하면 상위 필터와 같게 "데이터 없음"
      if (players.length === 0) {
        return null
      }
      data = buildChartData({
        settings: graphSettings,
        metricMap,
        selectedUnitList: unitList,
        players: { players },
        t,
        translateKey,
      })
    }
    // 리포트 PDF(sortGraphSectionForExport)와 같은 정렬 — 템플릿 정렬 또는 등번호 오름차순
    const sorted = sortChartRows(
      data.data,
      data.axisY,
      { key: data.sort.key, order: data.sort.order.toLowerCase() as "asc" | "desc" },
      positionRank,
    )

    return new ChartData(data.settings, data.sort, data.axisY, sorted)
  }, [
    playerSummary,
    graphSettings,
    metricMap,
    unitList,
    item.dataSource.playerIds,
    resolveItemPlayerIds,
    positionRank,
    isTrend,
    trendRows,
    trendPlayers,
    trendActivities,
    t,
    translateKey,
  ])

  // ── 세로 차트 배치 (2026-09-30 결정: 줄 단위 + 높이 자동) ──
  // 줄(선수 묶음)은 폭으로만 나누고(폭 ÷ 72px 명), 줄 높이는 막대 영역 + X축 이름 높이로 고정한다. 페이지 경계에
  // 걸리는 줄은 앞에 여백을 두어 다음 페이지 첫머리로 민다(planPageBlocks). 칸 높이는 이 내용을 따라간다.
  const verticalPlan = useMemo(() => {
    if (!chartData || width < 50) {
      return null
    }
    // 배치가 manual 이면 페이지 경계를 보지 않고 이어 그린다 — 사용자가 스페이서로 나눈다 (2026-10-01 결정)
    const planBlocks = (blocks: number[]) =>
      isManualLayout(item.options)
        ? { padTops: blocks.map(() => 0), totalHeight: blocks.reduce((sum, px) => sum + px, 0) }
        : planPageBlocks(blocks, item.y * GRID_ROW_PX + CANVAS_TOP_PX, pageHeight)
    // 세로(선수 세로 나열)
    if (isPlayerList) {
      // 그릴 때와 같은 축(선·점은 막대로 바꿔 그린다)으로 재야 칸 높이가 맞는다 (2026-10-01 보고: 세로 최소 높이 오류)
      const verticalAxisY = coerceVerticalAxisTypes(chartData.axisY)
      const rawTop =
        verticalTopPx > 0 ? verticalTopPx : ITEM_CHROME_PX + (showLegend ? LEGEND_FALLBACK_PX : 0)

      // manual: 장당 선수 수로 묶고 페이지 경계를 보지 않는다 — 사용자가 스페이서로 나눈다 (2026-10-01 결정)
      if (isManualLayout(item.options)) {
        const playerChunks = chunkVerticalRows(chartData.data, playersPerPage)
        const totalHeight = playerChunks.reduce(
          (sum, chunk, index) =>
            sum +
            (index === 0 ? rawTop : 0) +
            measureVerticalChartHeight(chunk.length, verticalAxisY, undefined, showPlayerListAxis),
          0,
        )

        return {
          chunks: [] as ChartRow[][],
          playerChunks,
          perChunkHeight: 0,
          xAxisLabelHeight: 0,
          labelOverflowLeft: 0,
          headGap: 0,
          padTops: playerChunks.map(() => 0),
          slots: playerChunks.map(() => 0),
          totalHeight,
        }
      }

      // auto: 표(Table)와 같이 남은 페이지에 들어가는 선수만 그리고 나머지는 다음 장 첫머리로 넘긴다 —
      // 장당 선수 수를 고정하면 페이지가 절반만 차거나 묶음이 한 장을 넘겼다 (2026-10-02 요청)
      const rowPx = rowHeightFor(verticalAxisY)
      const chromePx = measureVerticalChartHeight(0, verticalAxisY, undefined, showPlayerListAxis)
      const pages: Array<{ count: number; padTop: number; slotPx: number }> = []
      let cursor = item.y * GRID_ROW_PX + CANVAS_TOP_PX
      let pad = 0
      let headGap = 0
      let placed = 0
      let guard = 0
      while (placed < chartData.data.length && guard < 200) {
        guard += 1
        const pageIndex = Math.floor(cursor / pageHeight)
        const intoPage = cursor - pageIndex * pageHeight
        if (intoPage < PAGE_MARGIN_PX) {
          pad += PAGE_MARGIN_PX - intoPage
          cursor += PAGE_MARGIN_PX - intoPage
          continue
        }
        const pageBottom = (pageIndex + 1) * pageHeight - PAGE_MARGIN_PX
        const isFirst = pages.length === 0
        // 첫 묶음 위에는 머리말·범례가 함께 들어간다
        const head = chromePx + (isFirst ? rawTop : 0)
        const fit = Math.floor((pageBottom - cursor - head) / rowPx)
        if (fit < 1) {
          const nextTop = (pageIndex + 1) * pageHeight + PAGE_MARGIN_PX
          pad += nextTop - cursor
          cursor = nextTop
          continue
        }
        const count = Math.min(fit, chartData.data.length - placed)
        // 첫 묶음의 여백은 칸 위 여백(headGap)으로 빼 제목까지 같이 다음 장으로 내린다 (2026-09-30 요청).
        // 나머지 묶음은 자기 위 여백으로 들고 있는다
        if (isFirst) {
          headGap = pad
        }
        pages.push({
          count,
          padTop: isFirst ? 0 : pad,
          // 묶음 상자(.BuilderD3Chart__chart-row)의 높이다 — 첫 묶음 위의 머리말·범례(rawTop)는 상자 밖이라 뺀다
          slotPx: (pageIndex + 1) * pageHeight - (isFirst ? cursor + rawTop : cursor - pad),
        })
        placed += count
        pad = PAGE_MARGIN_PX
        cursor = (pageIndex + 1) * pageHeight + PAGE_MARGIN_PX
      }
      if (placed < chartData.data.length && pages.length) {
        // 한 장에 한 명도 안 들어가는 이상 상황 — 남은 선수는 마지막 묶음에 붙여 빠뜨리지 않는다
        pages[pages.length - 1].count += chartData.data.length - placed
      }

      let taken = 0
      const playerChunks = pages.map((page) => {
        const rows = chartData.data.slice(taken, taken + page.count)
        taken += page.count

        return rows
      })
      const totalHeight = pages.reduce(
        (sum, page, index) =>
          // 마지막 묶음은 뒤에 이어질 것이 없으므로 내용만큼만 — 빈 장이 하나 더 생기지 않게
          sum +
          page.padTop +
          (index === pages.length - 1
            ? measureVerticalChartHeight(page.count, verticalAxisY, undefined, showPlayerListAxis)
            : page.slotPx),
        headGap + rawTop,
      )

      return {
        chunks: [] as ChartRow[][],
        playerChunks: playerChunks.length ? playerChunks : [[]],
        perChunkHeight: 0,
        xAxisLabelHeight: 0,
        labelOverflowLeft: 0,
        headGap,
        padTops: pages.map((page) => page.padTop),
        slots: pages.map((page) => page.slotPx),
        totalHeight,
      }
    }
    const maxLabelLength = chartData.data.reduce((max, row) => {
      const originalLength = row.name?.length || 0

      return Math.max(max, originalLength > 20 ? 28 : originalLength)
    }, 0)
    const estimatedLabelWidth = maxLabelLength * 6.5
    const xAxisLabelHeight = Math.max(80, Math.ceil(estimatedLabelWidth * 0.707) + 20)
    const labelOverflowLeft = Math.max(0, Math.ceil(estimatedLabelWidth * 0.5) - 20)
    const drawingArea = width - labelOverflowLeft - 80
    const playersPerChunk = Math.max(2, Math.floor(drawingArea / 72))
    const chunks: ChartRow[][] = []
    for (let i = 0; i < chartData.data.length; i += playersPerChunk) {
      chunks.push(chartData.data.slice(i, i + playersPerChunk))
    }
    // 모아서 보기·그래프 위 값은 엔진이 차트 위 공간을 쓴다 — 그만큼 줄을 키워 막대 크기를 지킨다 (2026-10-01 요청)
    const perChunkHeight =
      Math.max(100, 30 * metrics.length) +
      xAxisLabelHeight +
      20 +
      dataLabelExtraPx(chartData.settings?.dataLabel, metrics.length)
    // 머리말·범례(rawTop)와 첫 줄을 한 블록으로 묶는다 — 제목만 페이지 끝에 남지 않게 (2026-09-30 요청).
    // 그 블록이 경계에 걸리면 여백(headGap)은 CanvasItem 위에 들어가 제목까지 같이 다음 페이지로 내려간다
    const rawTop =
      verticalTopPx > 0 ? verticalTopPx : ITEM_CHROME_PX + (showLegend ? LEGEND_FALLBACK_PX : 0)
    const blocks = chunks.map((_, index) =>
      index === 0 ? rawTop + perChunkHeight : perChunkHeight,
    )
    const { padTops, totalHeight } = planBlocks(blocks)

    return {
      chunks,
      playerChunks: [] as ChartRow[][],
      perChunkHeight,
      xAxisLabelHeight,
      labelOverflowLeft,
      headGap: padTops[0] ?? 0,
      padTops: [0, ...padTops.slice(1)],
      slots: chunks.map(() => 0),
      // 위 여백 + 머리말·범례 + 줄들 — 칸 높이는 이 값으로 정한다
      totalHeight,
    }
  }, [
    chartData,
    width,
    metrics.length,
    item.y,
    item.options.layoutMode,
    verticalTopPx,
    pageHeight,
    showLegend,
    isPlayerList,
    playersPerPage,
    showPlayerListAxis,
  ])

  // ── 높이: 내용(줄 × 고정 높이 + 페이지 여백)이 하한, 늘린 만큼은 아래 여백 ──
  // 세로 차트가 마지막으로 내용에 맞춰 정한 칸 높이 — 이 값과 같으면 "내용을 따라가는 중"으로 본다
  const lastAutoRowsRef = useRef<number | null>(null)

  useEffect(() => {
    if (!chartData || !currentPageId || width < 50 || isExporting) {
      return
    }

    if (!verticalPlan) {
      return
    }
    // totalHeight 에 위 여백·머리말·범례가 들어 있다. 칸 px = h×40−10 ≥ 내용 + 안쪽 여백 10
    const minH = Math.max(4, Math.ceil((verticalPlan.totalHeight + 20) / GRID_ROW_PX))
    // 내용 높이가 하한. 사용자가 그보다 늘린 높이는 지킨다(아래가 빈 여백) — 내용을 따라가던 칸(직전 자동값과 같음)만
    // 내용이 바뀔 때 같이 움직인다 (2026-09-30 요청)
    const followContent = lastAutoRowsRef.current !== null && item.h === lastAutoRowsRef.current
    const nextH = followContent ? minH : Math.max(item.h, minH)
    lastAutoRowsRef.current = minH

    const patch: { minH?: number; h?: number } = {}
    if (minH !== item.minH) {
      patch.minH = minH
    }
    if (nextH !== item.h) {
      patch.h = nextH
    }
    if (Object.keys(patch).length) {
      updateItemLayout(currentPageId, item.id, patch)
    }
  }, [
    chartData,
    width,
    verticalPlan,
    isExporting,
    currentPageId,
    item.id,
    item.h,
    item.minH,
    updateItemLayout,
  ])

  // ── Vertical chart using existing createChart engine (auto-split) ──
  const drawVerticalChart = useCallback(() => {
    if (!chartData || !verticalWrapperRef.current) {
      return
    }

    verticalWrapperRef.current.innerHTML = ""

    const chartWidth = width
    if (chartWidth < 50 || !verticalPlan) {
      return
    }
    // 세로(선수 세로 나열) — 공용 세로 렌더러로 묶음마다 SVG 하나. 모든 묶음이 같은 스케일을 쓰게 최대값을 넘긴다 (#185)
    if (isPlayerList) {
      const { highest, highestStackedSum } = collectVerticalHighest(chartData.data, chartData.axisY)
      const lastChunkIndex = verticalPlan.playerChunks.length - 1
      verticalPlan.playerChunks.forEach((rows, chunkIndex) => {
        const row = document.createElement("div")
        row.className = "BuilderD3Chart__chart-row"
        row.style.boxSizing = "border-box"
        row.style.paddingTop = `${verticalPlan.padTops[chunkIndex] ?? 0}px`
        // 묶음 하나가 출력 한 장 — 상자를 페이지 끝까지 고정해 다음 묶음이 다음 장 첫머리에서 시작한다.
        // 마지막 묶음과 내보내기(장 단위로 잘라 담는다)는 늘리지 않는다
        const slotPx = verticalPlan.slots[chunkIndex] ?? 0
        if (slotPx > 0 && chunkIndex < lastChunkIndex && !isExporting) {
          row.style.minHeight = `${slotPx}px`
        }
        drawVerticalPlayerChart(row, {
          rows,
          axisY: coerceVerticalAxisTypes(chartData.axisY),
          width: chartWidth,
          highest,
          highestStackedSum,
          showAxis: showPlayerListAxis,
          valuePositionType: dataLabelToValuePosition(chartData.settings?.dataLabel),
          decimalPoint: chartData.settings?.decimalPlace ?? null,
        })
        verticalWrapperRef.current?.appendChild(row)
      })

      return
    }
    const { chunks, perChunkHeight, xAxisLabelHeight, labelOverflowLeft, padTops } = verticalPlan

    // Prepare dataLabels mapping
    const dataLabelsMapping: Record<string, VALUE_POSITION_TYPE> = {
      hideValues: VALUE_POSITION_TYPE.HIDE_VALUES,
      insidePlots: VALUE_POSITION_TYPE.IN_BAR,
      abovePlots: VALUE_POSITION_TYPE.IN_CHART_EACH_POSITION,
      separateTable: VALUE_POSITION_TYPE.ABOVE,
    }

    // Render each chunk as an independent chart row
    chunks.forEach((chunkRows, chunkIndex) => {
      const adjustedChartWidth = chartWidth - labelOverflowLeft

      const makeStyle = (styleWidth: number) => {
        const chartStyle = new ChartStyle({
          width: styleWidth,
          height: perChunkHeight,
          flagShowChartLabelOnXAxis: false,
          flagLongNameOnXAxis: true,
        })
        // 이름 영역은 막대 영역 100px 을 남긴 만큼까지 쓴다 — 비율(35%)로 자르면 낮은 칸에서 이름이 잘렸다
        chartStyle.space.xAxisLabelHeight = Math.min(
          xAxisLabelHeight,
          Math.max(Math.floor(perChunkHeight * 0.35), perChunkHeight - 120),
        )
        // Tighten axis spacing for PDF builder
        chartStyle.space.yAxisLabelWidth = 24
        chartStyle.space.betweenXAxisAndLabelHeight = 6
        chartStyle.space.yAxisTickAndSpace = 8
        chartStyle.space.betweenRightYAxis = 6
        chartStyle.space.marginBetweenItemsRatio = 0.6
        chartStyle.fontSize = 9
        chartStyle.fontSizeValue = 9
        // 값 글자는 시리즈 색 + 흰 테두리 (2026-10-01 요청)
        chartStyle.valueOrnamentType = VALUE_ORNAMENT_TYPE.USE_CHART_COLOR

        if (chartData.settings?.dataLabel) {
          chartStyle.valuePositionType =
            dataLabelsMapping[chartData.settings.dataLabel] || VALUE_POSITION_TYPE.HIDE_VALUES
        }

        return chartStyle
      }

      const charts = [new ChartMultipleItem("chart", null, chunkRows)]
      const chartMultiple = new ChartMultiple(chartData.axisY, charts)

      // 누적 막대 등으로 축이 합쳐져 남는 폭은 항목에 나눠 가로를 채운다 (createChartFilled)
      const result = createChartFilled({
        targetWidth: adjustedChartWidth,
        makeStyle,
        chartMultiple,
        mouseover: () => {},
        mousemove: () => {},
        mouseout: () => {},
        translateKey,
        decimalPointForExportFile: chartData.settings?.decimalPlace ?? 1,
        t,
      })

      if (result) {
        const row = document.createElement("div")
        row.className = "BuilderD3Chart__chart-row"
        // 페이지 경계에 걸리는 줄은 앞에 빈 공간을 두어 다음 페이지 첫머리부터 그린다
        row.style.paddingTop = `${padTops[chunkIndex] ?? 0}px`

        const table = document.createElement("table")
        table.style.width = "100%"
        const tbody = document.createElement("tbody")
        const tr = document.createElement("tr")

        // Y-axis left
        const tdLeft = document.createElement("td")
        tdLeft.className = "BuilderD3Chart__y-axis-left"
        if (result.svgYAxisLeft) {
          // Tighten gap between axis label and tick numbers
          const vb = result.svgYAxisLeft.viewBox.baseVal
          const axisLabelEl = result.svgYAxisLeft.querySelector(":scope > text")
          const trimLeft = axisLabelEl ? 10 : 0
          if (axisLabelEl) {
            const currentY = parseFloat(axisLabelEl.getAttribute("y") || "0")
            axisLabelEl.setAttribute("y", String(currentY + trimLeft))
          }
          const newWidth = vb.width - trimLeft
          result.svgYAxisLeft.setAttribute("viewBox", `${trimLeft} 0 ${newWidth} ${vb.height}`)
          const div = document.createElement("div")
          div.style.width = `${newWidth}px`
          div.style.height = `${vb.height}px`
          div.appendChild(result.svgYAxisLeft)
          tdLeft.appendChild(div)
        }
        tr.appendChild(tdLeft)

        // Chart area
        const tdChart = document.createElement("td")
        const chartContainer = document.createElement("div")
        chartContainer.className = "BuilderD3Chart__chart-elements"
        chartContainer.style.paddingLeft = `${labelOverflowLeft}px`

        result.svgCharts.forEach((svgChart) => {
          if (svgChart) {
            const div = document.createElement("div")
            div.style.width = `${svgChart.viewBox.baseVal.width}px`
            div.style.height = `${svgChart.viewBox.baseVal.height}px`
            div.appendChild(svgChart)
            chartContainer.appendChild(div)
          }
        })
        tdChart.appendChild(chartContainer)
        tr.appendChild(tdChart)

        // Y-axis right
        const tdRight = document.createElement("td")
        tdRight.className = "BuilderD3Chart__y-axis-right"
        // 엔진이 자리만 잡고 비워 둔 오른쪽 축(누적 막대 2개)은 붙이지 않는다 — 폭을 차트가 쓴다
        if (hasDrawnContent(result.svgYAxisRight) && result.svgYAxisRight) {
          const div = document.createElement("div")
          div.style.width = `${result.svgYAxisRight.viewBox.baseVal.width}px`
          div.style.height = `${result.svgYAxisRight.viewBox.baseVal.height}px`
          div.appendChild(result.svgYAxisRight)
          tdRight.appendChild(div)
        }
        tr.appendChild(tdRight)

        tbody.appendChild(tr)
        table.appendChild(tbody)
        row.appendChild(table)
        verticalWrapperRef.current?.appendChild(row)
      }
    })
  }, [
    chartData,
    width,
    verticalPlan,
    showLegend,
    translateKey,
    t,
    isPlayerList,
    showPlayerListAxis,
    isExporting,
  ])

  // ── Draw effect ──
  useEffect(() => {
    drawVerticalChart()
  }, [drawVerticalChart])

  // 세로 차트 영역의 칸 안 오프셋(머리말·범례 아래) — 페이지 경계 계산과 칸 높이에 쓴다. 배치가 끝난 뒤 한 번 잰다
  // (창이 그려지지 않는 상태에서는 requestAnimationFrame 이 멈추므로 타이머를 함께 건다)
  useEffect(() => {
    let done = false
    const run = () => {
      if (done) {
        return
      }
      done = true
      const wrapper = verticalWrapperRef.current
      const grid = wrapper?.closest<HTMLElement>(".react-grid-item")
      if (!wrapper || !grid || grid.offsetHeight <= 0) {
        return
      }
      const gridRect = grid.getBoundingClientRect()
      const zoom = gridRect.height / grid.offsetHeight || 1
      // CanvasItem 에 적용된 위 여백(headGap)은 뺀다 — 여백 없는 순수 오프셋(머리말·범례)만 둔다
      const appliedGap = usePdfBuilderStore.getState().headGaps[item.id] ?? 0
      const top = Math.round(
        (wrapper.getBoundingClientRect().top - gridRect.top) / zoom - appliedGap,
      )
      setVerticalTopPx((prev) => (Math.abs(prev - top) > 1 ? top : prev))
    }
    const frame = requestAnimationFrame(run)
    const timer = setTimeout(run, 100)

    return () => {
      cancelAnimationFrame(frame)
      clearTimeout(timer)
    }
  }, [chartData, width, showLegend, item.options.title, item.id])

  // 첫 블록(머리말·범례 + 첫 줄)이 경계에 걸리면 그만큼 CanvasItem 위 여백으로
  useEffect(() => {
    setHeadGap(item.id, verticalPlan ? verticalPlan.headGap : 0)
  }, [verticalPlan, item.id, setHeadGap])

  // 액티비티를 막 골라 데이터를 받는 중 — "데이터 없음" 대신 스피너 (2026-10-01 보고)
  if (hasActivitySelection && isLoading && !(isTrend ? chartData : playerSummary)) {
    return (
      <div className="BuilderD3Chart BuilderD3Chart--empty loading">
        <LoadingSpinner />
      </div>
    )
  }

  // ── Empty state ──
  if ((!isTrend && !playerSummary) || metrics.length === 0 || !chartData) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: colors.neutral.grey["600"],
          fontSize: 13,
        }}>
        {!hasActivitySelection
          ? isTrend
            ? t("noActivityInPeriod", "No analyzed activities in the selected period")
            : t("selectActivity", "Select an activity from Report Filters")
          : !templateChart
            ? t("graphNotInTemplate", "The selected report template has no Graph section")
            : metrics.length > 0
              ? t("noData", "No data")
              : t("selectDataToDisplay", "Select data to display")}
      </div>
    )
  }

  return (
    <div
      className="BuilderD3Chart"
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}>
      {showLegend && (
        <div className="BuilderD3Chart__legend">
          {chartData?.axisY.map((y) => (
            <div key={y.key} className="BuilderD3Chart__legend-item" style={{ color: y.color }}>
              <span>●</span>
              {/* 범례에 단위를 붙인다 (2026-10-01 요청) */}
              <span>{`${translateKey(y.key)}${y.unit ? ` (${y.unit})` : ""}`}</span>
            </div>
          ))}
        </div>
      )}

      <div ref={verticalWrapperRef} className="BuilderD3Chart__vertical" />
    </div>
  )
}
